import { useCallback, useRef, useState } from "react"

import { uploadAttachment } from "../../Album/api/attachment.services.ts"
import { AttachmentModels } from "../../Album/types/attachment.types.ts"
import { readServerMessage } from "../helpers/server-message.ts"

export type UploadQueueStatus =
  | "queued"
  | "uploading"
  | "completed"
  | "failed"
  | "cancelled"

export interface UploadQueueItem {
  id: string
  file: File
  status: UploadQueueStatus
  progress: number
  error?: string
}

interface UseUploadQueueArgs {
  projectId: string
  folderId: string | null
  onUploaded: () => void | Promise<void>
}

// A handful of concurrent XHRs is fine for any real backend and keeps a
// 20-file drop from flooding the connection pool at once.
const MAX_CONCURRENT = 3

let nextId = 0
const makeId = () => `upload-${Date.now()}-${nextId++}`

export function useUploadQueue({ projectId, folderId, onUploaded }: UseUploadQueueArgs) {
  const [items, setItems] = useState<UploadQueueItem[]>([])
  const itemsRef = useRef(items)
  itemsRef.current = items

  // Items waiting their turn live here, not in React state — mutating this
  // from inside a `setState` updater would run twice under StrictMode's
  // double-invocation and queue every upload twice.
  const queueRef = useRef<UploadQueueItem[]>([])
  const controllers = useRef<Map<string, AbortController>>(new Map())
  const runningCount = useRef(0)
  const contextRef = useRef({ projectId, folderId })
  contextRef.current = { projectId, folderId }
  const drainRef = useRef<() => void>(() => {})

  const updateItem = useCallback((id: string, patch: Partial<UploadQueueItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }, [])

  const startUpload = useCallback(
    (item: UploadQueueItem) => {
      const controller = new AbortController()
      controllers.current.set(item.id, controller)
      updateItem(item.id, { status: "uploading", progress: 0, error: undefined })

      uploadAttachment(
        {
          file: item.file,
          object_id: contextRef.current.projectId,
          model_type: AttachmentModels.PROJECT,
          project_id: contextRef.current.projectId,
          ...(contextRef.current.folderId ? { folder: contextRef.current.folderId } : {})
        },
        controller.signal,
        (evt: { loaded: number; total?: number }) => {
          const total = evt.total ?? item.file.size
          updateItem(item.id, {
            progress: total ? Math.min(99, Math.round((evt.loaded / total) * 100)) : 0
          })
        }
      )
        .then(async (response) => {
          if (!response.ok) {
            updateItem(item.id, {
              status: controller.signal.aborted ? "cancelled" : "failed",
              error: controller.signal.aborted
                ? undefined
                : (readServerMessage(response.data) ?? undefined)
            })
            return
          }
          updateItem(item.id, { status: "completed", progress: 100 })
          await onUploaded()
        })
        .catch(() => {
          updateItem(item.id, {
            status: controller.signal.aborted ? "cancelled" : "failed"
          })
        })
        .finally(() => {
          controllers.current.delete(item.id)
          runningCount.current = Math.max(0, runningCount.current - 1)
          drainRef.current()
        })
    },
    [updateItem, onUploaded]
  )

  const drain = useCallback(() => {
    while (runningCount.current < MAX_CONCURRENT && queueRef.current.length > 0) {
      const next = queueRef.current.shift()
      if (!next) break
      runningCount.current += 1
      startUpload(next)
    }
  }, [startUpload])
  drainRef.current = drain

  const enqueue = useCallback(
    (files: File[]) => {
      if (!files.length) return
      const newItems: UploadQueueItem[] = files.map((file) => ({
        id: makeId(),
        file,
        status: "queued",
        progress: 0
      }))
      setItems((prev) => [...prev, ...newItems])
      queueRef.current.push(...newItems)
      drain()
    },
    [drain]
  )

  const cancelOne = useCallback(
    (id: string) => {
      const controller = controllers.current.get(id)
      if (controller) {
        controller.abort()
        return
      }
      queueRef.current = queueRef.current.filter((item) => item.id !== id)
      updateItem(id, { status: "cancelled" })
    },
    [updateItem]
  )

  const cancelAll = useCallback(() => {
    controllers.current.forEach((controller) => controller.abort())
    const stillQueued = queueRef.current
    queueRef.current = []
    stillQueued.forEach((item) => updateItem(item.id, { status: "cancelled" }))
  }, [updateItem])

  const retry = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((i) => i.id === id)
      if (!item || (item.status !== "failed" && item.status !== "cancelled")) return
      updateItem(id, { status: "queued", progress: 0, error: undefined })
      queueRef.current.push({ ...item, status: "queued", progress: 0, error: undefined })
      drain()
    },
    [drain, updateItem]
  )

  const dismiss = useCallback((id: string) => {
    controllers.current.get(id)?.abort()
    queueRef.current = queueRef.current.filter((item) => item.id !== id)
    setItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  return { items, enqueue, retry, cancelOne, cancelAll, dismiss }
}
